import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { logger } from '@/lib/monitoring/logger';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorCount: number;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to console
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Log to monitoring system
    logger.error('React Error Boundary caught error', error, {
      componentStack: errorInfo.componentStack,
    });

    // Send to Sentry without forcing it into initial bundle
    import('@/lib/monitoring/sentry')
      .then(({ captureError }) => {
        captureError(error, {
          errorBoundary: true,
          componentStack: errorInfo.componentStack,
        });
      })
      .catch(() => {
        // Ignore monitoring loader failures in boundary path.
      });

    // Update state with error info
    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null,
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Show different UI based on error count
      const isRecurring = this.state.errorCount > 2;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <CardTitle>
                  {isRecurring ? "Persistent Error Detected" : "Something went wrong"}
                </CardTitle>
              </div>
              <CardDescription>
                {isRecurring 
                  ? "We're having trouble recovering from this error. Please try going to the home page."
                  : "An unexpected error occurred. We've logged this issue and you can try recovering."
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Error details */}
              {this.state.error && import.meta.env.DEV && (
                <details className="p-3 bg-muted rounded text-sm">
                  <summary className="cursor-pointer font-medium mb-2">
                    Error Details (Development Only)
                  </summary>
                  <div className="space-y-2 font-mono text-xs overflow-auto max-h-40">
                    <div>
                      <strong>Error:</strong> {this.state.error.message}
                    </div>
                    {this.state.error.stack && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className="whitespace-pre-wrap mt-1">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Error count indicator */}
              {this.state.errorCount > 1 && (
                <div className="text-sm text-muted-foreground">
                  This error has occurred {this.state.errorCount} times.
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col gap-2">
                {!isRecurring && (
                  <Button
                    onClick={this.handleReset}
                    variant="default"
                    className="w-full"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                )}
                
                <Button
                  onClick={this.handleRefresh}
                  variant={isRecurring ? "default" : "outline"}
                  className="w-full"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Page
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="w-full"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Go to Home
                </Button>
              </div>

              {/* Support information */}
              <div className="text-xs text-muted-foreground text-center pt-2 border-t">
                If this problem persists, please contact support with the error details above.
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
